import React, { useState, useMemo } from 'react';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { KanbanColumn } from './KanbanColumn';
import { TaskCard } from './TaskCard';
import { TaskEditModal } from './TaskEditModal';

const COLUMNS = ['inbox', 'next_up', 'in_progress', 'done'];

export function KanbanBoard({ filteredTasks }) {
  const { tasks, moveTask, loading } = useWorkspace();
  const [activeTask, setActiveTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);

  // Use filtered tasks if provided, otherwise use all tasks
  const displayTasks = filteredTasks || tasks;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const tasksByColumn = useMemo(() => {
    const grouped = {
      inbox: [],
      next_up: [],
      in_progress: [],
      done: [],
    };

    displayTasks.forEach((task) => {
      const column = task.status || 'inbox';
      if (grouped[column]) {
        grouped[column].push(task);
      } else {
        grouped.inbox.push(task);
      }
    });

    // Sort by order within each column
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => a.order - b.order);
    });

    return grouped;
  }, [displayTasks]);

  const findColumn = (id) => {
    if (COLUMNS.includes(id)) return id;
    
    for (const [columnId, columnTasks] of Object.entries(tasksByColumn)) {
      if (columnTasks.find(task => task.id === id)) {
        return columnId;
      }
    }
    return null;
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const task = tasks.find(t => t.id === active.id);
    setActiveTask(task);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const activeColumn = findColumn(active.id);
    let overColumn = findColumn(over.id);

    // If dropped on a column itself
    if (COLUMNS.includes(over.id)) {
      overColumn = over.id;
    }

    if (!activeColumn || !overColumn) return;

    const activeTask = tasks.find(t => t.id === active.id);
    if (!activeTask) return;

    // Calculate new order
    const overColumnTasks = tasksByColumn[overColumn].filter(t => t.id !== active.id);
    let newOrder = 0;

    if (COLUMNS.includes(over.id)) {
      // Dropped on column - add to end
      newOrder = overColumnTasks.length;
    } else {
      // Dropped on another task - find its position
      const overTaskIndex = overColumnTasks.findIndex(t => t.id === over.id);
      newOrder = overTaskIndex >= 0 ? overTaskIndex : overColumnTasks.length;
    }

    // Only update if something changed
    if (activeColumn !== overColumn || activeTask.order !== newOrder) {
      try {
        await moveTask(active.id, overColumn, newOrder);
      } catch (error) {
        console.error('Error moving task:', error);
      }
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeColumn = findColumn(active.id);
    const overColumn = findColumn(over.id);

    if (!activeColumn || !overColumn || activeColumn === overColumn) return;
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
  };

  const handleCloseEdit = () => {
    setEditingTask(null);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center" data-testid="kanban-loading">
        <div className="text-zinc-500">Laddar uppgifter...</div>
      </div>
    );
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
      >
        <div className="flex gap-3 sm:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory" data-testid="kanban-board">
          {COLUMNS.map((columnId) => (
            <div key={columnId} className="min-w-[260px] sm:min-w-0 flex-1 snap-center">
              <KanbanColumn
                columnId={columnId}
                tasks={tasksByColumn[columnId]}
                onEditTask={handleEditTask}
              />
            </div>
          ))}
        </div>

        <DragOverlay>
          {activeTask && <TaskCard task={activeTask} overlay />}
        </DragOverlay>
      </DndContext>

      <TaskEditModal
        task={editingTask}
        isOpen={!!editingTask}
        onClose={handleCloseEdit}
      />
    </>
  );
}
